import { useCallback, useSyncExternalStore } from "react";
import { DEFAULT_ROUTE, parseStoredRoute, type RouteStop } from "./plannerLogic";

/**
 * The visitor's route, kept outside React so it survives going from the planner to a hostel page and back
 * (the pages are separate routes, so each remounts) and a reload (sessionStorage, tab-scoped).
 *
 * Server and first client render both see DEFAULT_ROUTE: `getServerSnapshot` is what React hydrates with, and
 * it swaps to the stored route right after, so a restored route never mismatches the server HTML.
 */

const STORAGE_KEY = "stippl_mm_route";

let current: RouteStop[] | null = null;
const listeners = new Set<() => void>();

function readStored(): RouteStop[] | null {
	try {
		return parseStoredRoute(window.sessionStorage.getItem(STORAGE_KEY));
	} catch {
		return null; // storage blocked: the route lives in memory for this page view
	}
}

const getSnapshot = (): RouteStop[] => {
	if (current === null) current = readStored() ?? DEFAULT_ROUTE;
	return current;
};

const getServerSnapshot = (): RouteStop[] => DEFAULT_ROUTE;

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export function setRoute(next: RouteStop[]): void {
	current = next;
	try {
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
	} catch {
		// storage blocked: see readStored
	}
	listeners.forEach((listener) => listener());
}

/** The route, and a setter that takes the next route or a function of the current one. */
export function useRoute(): [RouteStop[], (next: RouteStop[] | ((route: RouteStop[]) => RouteStop[])) => void] {
	const route = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
	const update = useCallback(
		(next: RouteStop[] | ((route: RouteStop[]) => RouteStop[])) => setRoute(typeof next === "function" ? next(getSnapshot()) : next),
		[]
	);
	return [route, update];
}
